import type { RuleField } from '@tabel/shared/schemas/rules';
import { CircleHelp, Lock } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { sameValue, secondsText } from './answer';
import { isTime, shownField } from './edit';
import { LABEL } from './texts';

/*
 * Поля внутри предложений ответа (004, T063; contracts/ui.md, «Изменение»; дизайн-система Choice, Field): число с
 * единицей и пределами, время «ЧЧ:ММ», вариант из 2–4 — радио, больше — список, да/нет — флажок. У каждого поля —
 * подпись для чтения с экрана (`aria-label` = простая подпись поля). Поле закона — замок и статья, значение не
 * меняется. На телефоне поле и строка варианта — не ниже 40 px, шрифт поля — 16 px.
 */

const BOX =
  'h-[30px] rounded-[7px] border bg-surface px-2 text-[14px] font-semibold text-ink tabular-nums transition-colors hover:border-line-strong focus:border-accent focus:ring-2 focus:ring-accent/15 focus:outline-none disabled:cursor-not-allowed disabled:bg-subtle disabled:text-ink-2 max-sm:h-10 max-sm:text-base';

/**
 * Подсказка поля: «?» рядом с подписью, нажали — текст под подписью, нажали ещё раз — скрыт (решение пользователя
 * 16.10.2026: подсказки были только в данных). Родитель — строка с переносом (`flex-wrap`): текст встаёт на всю ширину.
 */
export function HintToggle({ label, hint }: { label: string; hint?: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  if (!hint) return null;
  return (
    <>
      <button
        type="button"
        aria-label={`${LABEL.hint}: ${label}`}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-ink-3 hover:text-ink-2 max-sm:h-8 max-sm:w-8"
      >
        <CircleHelp size={14} strokeWidth={1.75} aria-hidden />
      </button>
      {open && (
        <span
          id={id}
          className="block basis-full text-[12.5px] leading-snug font-normal tracking-normal text-ink-2 normal-case"
        >
          {hint}
        </span>
      )}
    </>
  );
}

/** Замок и статья закона у поля, которое задаёт закон. */
export function LockMark({ article }: { article: string }) {
  return (
    <span
      data-lock
      className="inline-flex items-center gap-1 rounded-full bg-warn-soft px-2 py-0.5 align-baseline text-[12px] font-medium text-warn"
    >
      <Lock size={13} strokeWidth={1.75} aria-label={LABEL.lawLock} />
      <span>{article}</span>
    </span>
  );
}

/** Число, время или строка внутри предложения; рядом — единица, замок закона, прежнее значение зачёркнутым. */
export function InlineInput({
  field,
  raw,
  scale = 1,
  invalid,
  changed,
  was,
  describedBy,
  onChange,
}: {
  field: RuleField;
  raw: string;
  /** Единица ввода (`inputScale`): 60 — поле в секундах вводится в минутах. */
  scale?: number;
  invalid: boolean;
  changed: boolean;
  /** Значение действующей версии, если введено другое. */
  was?: string;
  describedBy?: string;
  onChange: (raw: string) => void;
}) {
  const time = isTime(field);
  const unit = shownField(field, scale).unit;
  // Длительность — ещё и словами: «3600 сек (1 ч)», «90 мин (1 ч 30 мин)».
  const seconds = field.unit === 'сек' ? Number(raw.replace(',', '.')) * scale : Number.NaN;
  const spoken = Number.isFinite(seconds) && seconds >= 60 * scale ? secondsText(seconds) : null;
  const width = time
    ? 'w-[70px] text-center'
    : field.control === 'text'
      ? 'w-[140px]'
      : 'w-[64px] text-center';
  const tone = invalid
    ? 'border-bad'
    : changed
      ? 'border-accent bg-accent-soft text-accent'
      : 'border-line-strong';
  return (
    <span data-field className="inline-flex flex-wrap items-baseline gap-1 align-baseline">
      <input
        type="text"
        inputMode={time ? 'numeric' : field.control === 'text' ? 'text' : 'decimal'}
        autoComplete="off"
        aria-label={field.label}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        placeholder={time ? 'ЧЧ:ММ' : undefined}
        maxLength={time ? 5 : undefined}
        disabled={field.locked !== undefined}
        value={field.locked ? String(field.locked.value ?? raw) : raw}
        onChange={(e) => onChange(e.target.value)}
        className={`${BOX} ${width} ${tone}`}
      />
      {unit && <span className="text-ink-2">{unit}</span>}
      {spoken && <span className="text-[12.5px] text-ink-3">({spoken})</span>}
      {field.locked && <LockMark article={field.locked.article} />}
      {was !== undefined && changed && (
        <s className="text-[12px] font-normal text-ink-3" aria-hidden>
          {was}
        </s>
      )}
    </span>
  );
}

/** Вариант из 5 и больше — список внутри предложения. */
export function InlineSelect({
  field,
  value,
  changed,
  onChange,
  optionLabel,
}: {
  field: RuleField;
  value: unknown;
  changed: boolean;
  onChange: (value: unknown) => void;
  /** Своя подпись варианта (например, «Не применять» у значения `offWhen`); нет — описание варианта. */
  optionLabel?: (value: unknown) => string | undefined;
}) {
  const options = field.options ?? [];
  return (
    <span data-field className="inline-flex items-baseline gap-1 align-baseline">
      <select
        aria-label={field.label}
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => {
          const picked = options.find((o) => String(o.value) === e.target.value);
          if (picked) onChange(picked.value);
        }}
        className={`${BOX} max-w-full pr-7 ${changed ? 'border-accent bg-accent-soft text-accent' : 'border-line-strong'}`}
      >
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)}>
            {optionLabel?.(o.value) ?? o.doc ?? String(o.value)}
          </option>
        ))}
      </select>
    </span>
  );
}

/** Строка радио: подпись (вариант словами) и что будет при этом варианте. */
export interface RadioRow {
  readonly value: unknown;
  /** Имя варианта для чтения с экрана. */
  readonly label: string;
  /** Предложение варианта; у выбранного — с полями. */
  readonly content: ReactNode;
  /** В предложении есть поля: строка не `label`, чтобы нажатие на поле не меняло вариант. */
  readonly hasFields?: boolean;
}

/**
 * Вариант из 2–4 — радио (дизайн-система Choice). Предложения вариантов — строками; короткие варианты внутри
 * предложения — в строку. Невыбранный вариант — серым, нажатие на его текст выбирает его.
 */
export function ChoiceRadios({
  label,
  value,
  rows,
  inline,
  onPick,
  caption,
  hint,
}: {
  label: string;
  value: unknown;
  rows: readonly RadioRow[];
  inline: boolean;
  onPick: (value: unknown) => void;
  /**
   * Подпись блока на экране (название поля): в ответе несколько блоков вариантов — видно, какой вариант к какому
   * вопросу (приёмка 16.10.2026: 8 вариантов трёх вопросов одним списком). У вариантов внутри фразы подписи нет.
   */
  caption?: boolean;
  /** Подсказка поля: «?» у подписи блока. */
  hint?: string;
}) {
  const name = useId();
  return (
    <span
      role="radiogroup"
      aria-label={label}
      className={
        inline
          ? 'inline-flex flex-wrap items-baseline gap-x-3 gap-y-0.5 align-baseline'
          : 'my-1 flex flex-col gap-1'
      }
    >
      {/* Подпись — когда блоков несколько или у поля есть подсказка: «?» нужно куда-то поставить. */}
      {(caption || hint) && !inline && (
        <span className="mt-1.5 flex flex-wrap items-center gap-x-1">
          <span aria-hidden className="text-[11.5px] font-semibold tracking-[0.05em] text-ink-3 uppercase">
            {label}
          </span>
          <HintToggle label={label} hint={hint} />
        </span>
      )}
      {rows.map((r, i) => {
        const on = sameValue(r.value, value);
        const id = `${name}-${i}`;
        const text = <span className={on ? 'text-ink' : 'text-ink-3'}>{r.content}</span>;
        return (
          <span
            key={id}
            className={`flex items-baseline gap-2.5 max-sm:min-h-10 ${inline ? 'inline-flex' : ''}`}
          >
            <input
              id={id}
              type="radio"
              name={name}
              aria-label={r.label}
              checked={on}
              onChange={() => onPick(r.value)}
              className="relative top-[2px] h-4 w-4 shrink-0 accent-accent"
            />
            {on && r.hasFields ? (
              text
            ) : (
              <label htmlFor={id} className="cursor-pointer">
                {text}
              </label>
            )}
          </span>
        );
      })}
    </span>
  );
}

/** Да/нет — флажок (дизайн-система Choice); текст рядом — что делает система, если флажок стоит. */
export function ChoiceCheck({
  label,
  checked,
  onChange,
  children,
  hasFields,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  hasFields?: boolean;
  /** Выбрать нельзя: расчёт этот вариант пока не учитывает. */
  disabled?: boolean;
}) {
  const id = useId();
  const text = <span className={checked ? 'text-ink' : 'text-ink-3'}>{children}</span>;
  return (
    <span className="my-0.5 flex items-baseline gap-2.5 max-sm:min-h-10">
      <input
        id={id}
        type="checkbox"
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="relative top-[2px] h-4 w-4 shrink-0 accent-accent disabled:opacity-50"
      />
      {checked && hasFields ? (
        text
      ) : (
        <label htmlFor={id} className="cursor-pointer">
          {text}
        </label>
      )}
    </span>
  );
}

/** Вариант значения словами: описание варианта или само значение. */
export function optionText(field: RuleField, value: unknown): string {
  return field.options?.find((o) => sameValue(o.value, value))?.doc ?? String(value);
}
